import React, { useState, useEffect, useRef } from 'react';
import { ShoppingCart, X, Trash2, Play, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';
import API from '../services/api';

export default function OrderBucket({
  bucket,
  refreshBucket,
  onExecuted,
  onToast,
  apiBase = '/bucket',
  title = 'Order Bucket',
  subtitle = 'Add orders, then execute all at once',
  emptyText = 'Your bucket is empty',
  emptyHint = 'Click Buy or Sell on any player to add orders here',
}) {
  const [open, setOpen]         = useState(false);
  const [executing, setExecuting] = useState(false);
  const [bouncing, setBouncing] = useState(false);
  const prevCountRef = useRef(bucket?.orders?.length || 0);

  useEffect(() => {
    refreshBucket?.();
    const interval = setInterval(() => refreshBucket?.(), 30000);
    return () => clearInterval(interval);
  }, [refreshBucket]);

  const orderCount = bucket?.orders?.length || 0;
  const summary    = bucket?.summary;

  useEffect(() => {
    if (orderCount > prevCountRef.current) {
      setBouncing(true);
      const timer = setTimeout(() => setBouncing(false), 550);
      prevCountRef.current = orderCount;
      return () => clearTimeout(timer);
    }
    prevCountRef.current = orderCount;
  }, [orderCount]);

  const removeOrder = async (id) => {
    try {
      await API.delete(`${apiBase}/${id}`);
      refreshBucket?.();
      onToast?.('Order removed from bucket', 'info');
    } catch (err) {
      onToast?.(err.response?.data?.error || 'Failed to remove', 'error');
    }
  };

  const clearBucket = async () => {
    try {
      await API.delete(`${apiBase}/clear/all`);
      refreshBucket?.();
      onToast?.('Bucket cleared', 'info');
    } catch (err) {
      onToast?.('Failed to clear', 'error');
    }
  };

  const executeBucket = async () => {
    setExecuting(true);
    try {
      const r = await API.post(`${apiBase}/execute`);
      const d = r.data;
      onToast?.(d.message, d.failed?.length > 0 ? 'warning' : 'success');
      refreshBucket?.();
      onExecuted?.(d);
      if (d.executed?.length > 0) setOpen(false);
    } catch (err) {
      onToast?.(err.response?.data?.error || 'Execution failed', 'error');
    } finally {
      setExecuting(false);
    }
  };

  return (
    <>
      {/* Floating bucket button */}
      <button
        onClick={() => { setOpen(true); refreshBucket?.(); }}
        style={{
          position: 'fixed', bottom: 80, right: 20,
          width: 52, height: 52, borderRadius: '50%',
          background: orderCount > 0 ? 'var(--accent)' : 'var(--bg-elevated)',
          border: `1px solid ${orderCount > 0 ? 'transparent' : 'var(--border)'}`,
          color: orderCount > 0 ? '#fff' : 'var(--text-secondary)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: orderCount > 0 ? '0 4px 20px rgba(245,158,11,0.4)' : '0 2px 8px rgba(0,0,0,0.3)',
          zIndex: 150, transition: 'all 0.2s',
          animation: bouncing ? 'bucketBounce 0.55s ease' : 'none',
        }}
        title="Order Bucket"
      >
        <ShoppingCart size={20} />
        {orderCount > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            background: 'var(--red)', color: '#fff',
            width: 20, height: 20, borderRadius: '50%',
            fontSize: '0.68rem', fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid var(--bg-primary)',
          }}>
            {orderCount}
          </span>
        )}
      </button>

      {/* Slide-in panel */}
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200 }}
          />
          <div style={{
            position: 'fixed', right: 0, top: 0, bottom: 0,
            width: '100%', maxWidth: 420,
            background: 'var(--bg-card)',
            borderLeft: '1px solid var(--border)',
            zIndex: 201, display: 'flex', flexDirection: 'column',
            animation: 'slideInRight 0.2s ease',
          }}>
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 700 }}>
                  {title}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  {subtitle}
                </div>
              </div>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4 }}>
                <X size={20} />
              </button>
            </div>

            {/* Orders list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
              {orderCount === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-muted)' }}>
                  <ShoppingCart size={40} style={{ opacity: 0.25, marginBottom: 12 }} />
                  <p style={{ fontSize: '0.87rem' }}>{emptyText}</p>
                  <p style={{ fontSize: '0.77rem', marginTop: 6 }}>{emptyHint}</p>
                </div>
              ) : (
                <>
                  {bucket.orders.map(order => {
                    const priceChanged = Math.abs(parseFloat(order.price_change || 0)) > 0.5;
                    const isBuy  = order.type === 'buy';
                    return (
                      <div key={order.id} style={{
                        background: 'var(--bg-secondary)', borderRadius: 10,
                        padding: '10px 12px', marginBottom: 8,
                        border: `1px solid ${isBuy ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
                        display: 'flex', alignItems: 'center', gap: 10,
                      }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                          background: isBuy ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                          color: isBuy ? 'var(--green)' : 'var(--red)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.7rem', fontWeight: 700,
                        }}>
                          {isBuy ? 'BUY' : 'SELL'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                            {order.player_name}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 1 }}>
                            Added @ {parseFloat(order.price_at_add).toFixed(1)} pts
                            {priceChanged && (
                              <span style={{ marginLeft: 6, color: parseFloat(order.price_change) > 0 ? 'var(--green)' : 'var(--red)' }}>
                                · Now {parseFloat(order.current_stock_price).toFixed(1)}
                                {parseFloat(order.price_change) > 0 ? <TrendingUp size={10} style={{ display: 'inline', marginLeft: 2 }} /> : <TrendingDown size={10} style={{ display: 'inline', marginLeft: 2 }} />}
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.95rem', color: isBuy ? 'var(--red)' : 'var(--green)', flexShrink: 0 }}>
                          {isBuy ? '-' : '+'}{parseFloat(order.current_stock_price).toFixed(1)}
                        </div>
                        <button onClick={() => removeOrder(order.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, flexShrink: 0 }}>
                          <X size={14} />
                        </button>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* Summary + execute */}
            {orderCount > 0 && summary && (
              <div style={{ padding: '14px 16px', borderTop: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                {/* Summary rows */}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 6 }}>
                  <span>Buy cost</span>
                  <span style={{ color: 'var(--red)', fontWeight: 600 }}>-{summary.totalBuyCost.toFixed(1)} pts</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 6 }}>
                  <span>Sell proceeds</span>
                  <span style={{ color: 'var(--green)', fontWeight: 600 }}>+{summary.totalSellValue.toFixed(1)} pts</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6, paddingTop: 6, borderTop: '1px solid var(--border)' }}>
                  <span>Available cash</span>
                  <span>{summary.availablePoints.toFixed(1)} pts</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', fontWeight: 700, marginBottom: 12 }}>
                  <span>After execution</span>
                  <span style={{ color: (summary.availablePoints - summary.totalBuyCost + summary.totalSellValue) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                    {(summary.availablePoints - summary.totalBuyCost + summary.totalSellValue).toFixed(1)} pts
                  </span>
                </div>

                {!summary.canExecute && (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '8px 10px', marginBottom: 10, fontSize: '0.75rem', color: 'var(--red)' }}>
                    <AlertCircle size={13} />
                    Insufficient points for all buy orders. Remove some buys or add sell orders.
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={clearBucket}
                    style={{ flex: 1, padding: '9px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.82rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}
                  >
                    <Trash2 size={13} /> Clear
                  </button>
                  <button
                    onClick={executeBucket}
                    disabled={executing || !summary.canExecute}
                    style={{ flex: 2, padding: '9px', borderRadius: 8, border: 'none', background: summary.canExecute ? 'var(--accent)' : 'var(--bg-elevated)', color: summary.canExecute ? '#fff' : 'var(--text-muted)', cursor: summary.canExecute ? 'pointer' : 'not-allowed', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: 'var(--font-display)' }}
                  >
                    {executing ? '...' : <><Play size={14} /> Execute {orderCount} Order{orderCount > 1 ? 's' : ''}</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
        @keyframes bucketBounce {
          0% { transform: scale(1); }
          28% { transform: scale(1.16); }
          52% { transform: scale(0.93); }
          76% { transform: scale(1.08); }
          100% { transform: scale(1); }
        }
      `}</style>
    </>
  );
}
